import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, Chip, Alert, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Fab, useMediaQuery, IconButton, Drawer, TextField, FormControl, InputLabel, Select, MenuItem, Divider, Stack, Paper } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridActionsCellItem, GridToolbar, GridCallbackDetails } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SchoolIcon from '@mui/icons-material/School';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import EditStudentDialog from '../components/EditStudentDialog';
import AddStudentDialog from '../components/AddStudentDialog';
import { DataGridSkeleton } from '../components/LoadingSkeletons';
import CustomNoRowsOverlay from '../components/CustomNoRowsOverlay';
import PageHeader from '../components/PageHeader';
import Avatar from '@mui/material/Avatar';
import { useTheme, ThemeProvider } from '@mui/material/styles';
import { Tooltip as MuiTooltip } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import Papa from 'papaparse';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot, TimelineOppositeContent } from '@mui/lab';
import FilterListIcon from '@mui/icons-material/FilterList';
import dayjs from 'dayjs';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import { getUnlinkedStudents, onboardStudent } from '../services/studentService';
import { Student } from '../types/index';

const COLUMN_VIS_KEY = 'students_column_visibility';
const defaultColumnVisibility = {
  date_of_birth: false,
  gender: false,
  enrollment_date: false,
  parent_email: false,
  parent_phone: false,
};

const Students: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [editStudent, setEditStudent] = useState<Student | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [error, setError] = useState('');
  const { isAuthenticated, user } = useAuth();
  const schoolId = user?.school_id;
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterGender, setFilterGender] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({ open: false, message: '', severity: 'success' });
  const [detailStudent, setDetailStudent] = useState<Student | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importError, setImportError] = useState('');
  const [activityTimeline, setActivityTimeline] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem(COLUMN_VIS_KEY);
    return saved ? JSON.parse(saved) : defaultColumnVisibility;
  });
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [totalStudents, setTotalStudents] = useState(0);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterEnrollmentStart, setFilterEnrollmentStart] = useState<string | null>(null);
  const [filterEnrollmentEnd, setFilterEnrollmentEnd] = useState<string | null>(null);
  const [newTag, setNewTag] = useState('');
  const requiredFields = ['name', 'class_level', 'status'];
  const acceptedStatus = ['active', 'inactive', 'graduated', 'transferred'];
  const acceptedGender = ['Male', 'Female', 'Other'];
  const [importSummary, setImportSummary] = useState<{ success: number; failed: number; failedRows: any[] }>({ success: 0, failed: 0, failedRows: [] });
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [unlinkedStudents, setUnlinkedStudents] = useState<Student[]>([]);
  const [onboarding, setOnboarding] = useState<{ [id: number]: boolean }>({});
  const [onboardError, setOnboardError] = useState<string | null>(null);
  const [onboardSuccess, setOnboardSuccess] = useState<string | null>(null);
  const [onboardForm, setOnboardForm] = useState<{ [id: number]: { username: string; email: string; password: string } }>({});
  const classLevels = [
    'Pre-Primary 1', 'Pre-Primary 2',
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8',
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];
  const [onboardClassLevel, setOnboardClassLevel] = useState<string>('');

  const fetchStudents = useCallback(async () => {
    if (!isAuthenticated || !schoolId) return;
    setLoading(true);
    try {
      const params: any = {
        school_id: schoolId,
        page: paginationModel?.page + 1,
        page_size: paginationModel?.pageSize,
      };
      if (filterClass) params.class_level = filterClass;
      if (filterStatus) params.status = filterStatus;
      if (filterGender) params.gender = filterGender;
      if (filterEnrollmentStart) params.enrollment_start = filterEnrollmentStart;
      if (filterEnrollmentEnd) params.enrollment_end = filterEnrollmentEnd;
      const response = await api.get(`/students/`, { params });
      setStudents((response.data as any).results || response.data as any[]);
      setTotalStudents((response.data as any).count || (response.data as any[]).length || 0);
    } catch (err) {
      setError('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, schoolId, paginationModel, filterClass, filterStatus, filterGender, filterEnrollmentStart, filterEnrollmentEnd]);

  const fetchUnlinkedStudents = async (classLevel?: string) => {
    if (!schoolId) return;
    try {
      let data = await getUnlinkedStudents(schoolId);
      if (classLevel) {
        data = data.filter((s: Student) => s.class_level === classLevel);
      }
      setUnlinkedStudents(data);
      // Prefill forms
      const forms: any = {};
      data.forEach((s: Student) => {
        forms[s.id] = {
          username: s.name.toLowerCase().replace(/\s+/g, '_') + '_' + s.id,
          email: s.parent_email || '',
          password: '',
        };
      });
      setOnboardForm(forms);
    } catch (e: any) {
      setOnboardError('Failed to load unlinked students');
    }
  };

  const handleOpenOnboardDialog = () => {
    setOnboardDialogOpen(true);
    fetchUnlinkedStudents(onboardClassLevel);
  };
  const handleCloseOnboardDialog = () => {
    setOnboardDialogOpen(false);
    setUnlinkedStudents([]);
    setOnboardForm({});
    setOnboardError(null);
    setOnboardSuccess(null);
  };
  const handleOnboardClassLevelChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    setOnboardClassLevel(e.target.value as string);
    fetchUnlinkedStudents(e.target.value as string);
  };

  const handleOnboard = async (studentId: number) => {
    setOnboarding(prev => ({ ...prev, [studentId]: true }));
    setOnboardError(null);
    setOnboardSuccess(null);
    try {
      const { username, email, password } = onboardForm[studentId];
      if (!username || !email || !password) {
        setOnboardError('All fields are required');
        setOnboarding(prev => ({ ...prev, [studentId]: false }));
        return;
      }
      await onboardStudent(studentId, { username, email, password });
      setOnboardSuccess('Student onboarded successfully!');
      // Remove onboarded student from list
      setUnlinkedStudents(prev => prev.filter(s => s.id !== studentId));
    } catch (e: any) {
      setOnboardError(e?.response?.data?.detail || 'Failed to onboard student');
    } finally {
      setOnboarding(prev => ({ ...prev, [studentId]: false }));
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    if (detailStudent) {
      setTimelineLoading(true);
      api.get(`/attendance/report?student_id=${detailStudent.id}`)
        .then(res => setActivityTimeline(res.data as any[]))
        .catch(() => setActivityTimeline([]))
        .catch(() => setTimelineLoading(false));
    }
  }, [detailStudent]);

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/students/${id}`);
      fetchStudents();
    } catch {
      setError('Failed to delete student');
    }
    setDeleteConfirmation({ open: false, id: null });
  };

  const openDeleteDialog = (id: number) => {
    setDeleteConfirmation({ open: true, id: id });
  };
  const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const columns: GridColDef[] = [
    {
      field: 'profile_picture',
      headerName: 'Profile Picture',
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.value ? (
          <Avatar src={backendUrl + params.value} alt="Profile" sx={{ width: 40, height: 40 }} />
        ) : (
          <Avatar sx={{ width: 40, height: 40 }}>{params.row.name?.[0] || '?'}</Avatar>
        ),
    },
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1, 
      minWidth: 150,
      filterable: true,
    },
    { 
      field: 'student_id', 
      headerName: 'Student ID', 
      width: 120,
      filterable: true,
    },
    { 
      field: 'class_level', 
      headerName: 'Class Level', 
      width: 130,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Pre-Primary 1', 'Pre-Primary 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'],
      editable: true,
    },
    { 
      field: 'date_of_birth', 
      headerName: 'Date of Birth', 
      width: 130,
      filterable: true,
      type: 'date',
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : '-'
    },
    { 
      field: 'gender', 
      headerName: 'Gender', 
      width: 100,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['Male', 'Female', 'Other'],
    },
    { 
      field: 'enrollment_date', 
      headerName: 'Enrollment Date', 
      width: 140,
      filterable: true,
      type: 'date',
      renderCell: (params) => params.value ? new Date(params.value).toLocaleDateString() : '-'
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      filterable: true,
      type: 'singleSelect',
      valueOptions: ['active', 'inactive', 'graduated', 'transferred'],
      editable: true,
      renderCell: (params) => {
        const icon = statusIcon(params.value);
        return icon ? (
          <Chip
            icon={icon}
            label={params.value || 'Active'}
            color={
              params.value === 'active' ? 'success' :
              params.value === 'graduated' ? 'primary' :
              params.value === 'inactive' ? 'error' :
              params.value === 'transferred' ? 'warning' : 'default'
            }
            size="small"
          />
        ) : (
          <Chip
            label={params.value || 'Active'}
            color={
              params.value === 'active' ? 'success' :
              params.value === 'graduated' ? 'primary' :
              params.value === 'inactive' ? 'error' :
              params.value === 'transferred' ? 'warning' : 'default'
            }
            size="small"
          />
        );
      }
    },
    { 
      field: 'parent_email', 
      headerName: 'Parent Email', 
      width: 180,
      filterable: true,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'parent_phone', 
      headerName: 'Parent Phone', 
      width: 140,
      filterable: true,
      renderCell: (params) => params.value || '-'
    },
    { 
      field: 'images', 
      headerName: 'Images', 
      width: 100,
      filterable: true,
      type: 'number',
      renderCell: (params) => (
        <Chip 
          label={params.value.length} 
          color={params.value.length > 0 ? 'success' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {(params.value || []).map((tag: string, idx: number) => (
            <Chip key={idx} label={tag} size="small" color="info" />
          ))}
        </Box>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      filterable: false,
      getActions: (params) => [
        <React.Fragment key="edit">
          <Tooltip title="Edit" arrow>
            <span>
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                aria-label="Edit"
                onClick={() => setEditStudent(params.row)}
              />
            </span>
          </Tooltip>
        </React.Fragment>,
        <React.Fragment key="delete">
          <Tooltip title="Delete" arrow>
            <span>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                aria-label="Delete"
                onClick={() => openDeleteDialog(params.row.id)}
              />
            </span>
          </Tooltip>
        </React.Fragment>,
      ],
    },
  ];

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(search.toLowerCase()) || (student.student_id?.toLowerCase().includes(search.toLowerCase()) ?? false);
    const matchesClass = !filterClass || student.class_level === filterClass;
    const matchesStatus = !filterStatus || student.status === filterStatus;
    const matchesGender = !filterGender || student.gender === filterGender;
    return matchesSearch && matchesClass && matchesStatus && matchesGender;
  });

  const statusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />;
      case 'graduated': return <SchoolIcon color="primary" fontSize="small" sx={{ mr: 0.5 }} />;
      case 'inactive': return <CancelIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />;
      case 'transferred': return <InfoIcon color="warning" fontSize="small" sx={{ mr: 0.5 }} />;
      default: return null;
    }
  };

  // Export students as CSV
  const handleExportCSV = () => {
    const csv = Papa.unparse(students.map(({ images, ...rest }) => rest));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'students.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download CSV template for bulk import
  const handleDownloadTemplate = () => {
    const headers = [
      'name',
      'student_id',
      'class_level',
      'date_of_birth',
      'gender',
      'enrollment_date',
      'status',
      'parent_email',
      'parent_phone',
      'tags'
    ];
    const sampleRow = {
      name: 'John Doe',
      student_id: 'STU123',
      class_level: 'Grade 1',
      date_of_birth: '2012-05-10',
      gender: 'Male',
      enrollment_date: '2020-01-15',
      status: 'active',
      parent_email: 'parent@example.com',
      parent_phone: '+1234567890',
      tags: 'scholar,football'
    };
    const csv = Papa.unparse({
      fields: headers,
      data: [sampleRow]
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'students_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper to convert DD/MM/YYYY to YYYY-MM-DD
  function convertDateString(dateStr: string) {
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  }

  // Enhanced Import students from CSV
  const handleImportCSV = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data as any[];
        const failedRows: any[] = [];
        let success = 0;
        // Prepare all students for bulk import
        const studentsToImport = data.map(student => ({
          ...student,
          school_id: schoolId, // from context
          images: [], // always an empty array for import
          tags: student.tags ? student.tags.split(',').map((t: string) => t.trim()).filter(Boolean) : [],
          date_of_birth: student.date_of_birth ? convertDateString(student.date_of_birth) : undefined,
          enrollment_date: student.enrollment_date ? convertDateString(student.enrollment_date) : undefined,
          graduation_date: student.graduation_date ? convertDateString(student.graduation_date) : undefined,
        }));
        try {
          await api.post('/students/bulk_import', studentsToImport);
          success = studentsToImport.length;
        } catch (err: any) {
          // If the whole request fails, mark all as failed
          failedRows.push(...studentsToImport.map(s => ({ ...s, error: err?.response?.data?.detail || 'Bulk import failed' })));
        }
        setImportSummary({ success, failed: failedRows.length, failedRows });
        setSnackbar({ open: true, message: `${success} students imported, ${failedRows.length} failed.`, severity: failedRows.length ? 'error' : 'success' });
        fetchStudents();
        setImportDialogOpen(false);
      },
      error: () => setImportError('Failed to parse CSV file.'),
    });
  };

  // Download failed rows as CSV
  const handleDownloadFailedRows = () => {
    if (!importSummary.failedRows.length) return;
    const csv = Papa.unparse(importSummary.failedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'failed_students.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Save column visibility changes
  const handleColumnVisibilityChange = (model: any) => {
    setColumnVisibilityModel(model);
    localStorage.setItem(COLUMN_VIS_KEY, JSON.stringify(model));
  };

  // Inline editing handler
  const processRowUpdate = async (newRow: Student, oldRow: Student) => {
    try {
      await api.put(`/students/${newRow.id}`, {
        status: newRow.status,
        class_level: newRow.class_level,
      });
      setSnackbar({ open: true, message: 'Student updated', severity: 'success' });
      return newRow;
    } catch {
      setSnackbar({ open: true, message: 'Failed to update student', severity: 'error' });
      return oldRow;
    }
  };

  const handleAddTag = async () => {
    if (!detailStudent || !newTag.trim()) return;
    const updatedTags = [...(detailStudent.tags || []), newTag.trim()];
    try {
      await api.put(`/students/${detailStudent.id}`, { tags: updatedTags });
      setDetailStudent({ ...detailStudent, tags: updatedTags });
      setNewTag('');
      setSnackbar({ open: true, message: 'Tag added', severity: 'success' });
      fetchStudents();
    } catch {
      setSnackbar({ open: true, message: 'Failed to add tag', severity: 'error' });
    }
  };

  const handleDeleteTag = async (tagToDelete: string) => {
    if (!detailStudent) return;
    const updatedTags = (detailStudent.tags || []).filter((tag: string) => tag !== tagToDelete);
    try {
      await api.put(`/students/${detailStudent.id}`, { tags: updatedTags });
      setDetailStudent({ ...detailStudent, tags: updatedTags });
      setSnackbar({ open: true, message: 'Tag removed', severity: 'success' });
      fetchStudents();
    } catch {
      setSnackbar({ open: true, message: 'Failed to remove tag', severity: 'error' });
    }
  };

  const RenderDetailStudentDialog: React.FC = () => {
    if (!detailStudent) return null;
    const detailStatusIcon = statusIcon(detailStudent.status || '');
    return (
      <ThemeProvider theme={theme}>
        <Dialog open={!!detailStudent} onClose={() => setDetailStudent(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Student Details</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
              <Avatar src={backendUrl + detailStudent.profile_picture} alt="Profile" sx={{ width: 80, height: 80, mb: 1, cursor: 'pointer' }} onClick={() => setLightboxOpen(true)} />
              <Typography variant="h6">{detailStudent.name}</Typography>
              <Typography variant="body2" color="text.secondary">{detailStudent.student_id}</Typography>
              <Chip label={detailStudent.class_level} sx={{ mt: 1 }} />
              {detailStatusIcon
                ? <Chip icon={detailStatusIcon} label={detailStudent.status} sx={{ mt: 1 }} />
                : <Chip label={detailStudent.status} sx={{ mt: 1 }} />
              }
            </Box>
            <Typography variant="subtitle2">Parent Email: {detailStudent.parent_email || '-'}</Typography>
            <Typography variant="subtitle2">Parent Phone: {detailStudent.parent_phone || '-'}</Typography>
            <Typography variant="subtitle2">Gender: {detailStudent.gender || '-'}</Typography>
            <Typography variant="subtitle2">Date of Birth: {detailStudent.date_of_birth ? new Date(detailStudent.date_of_birth).toLocaleDateString() : '-'}</Typography>
            <Typography variant="subtitle2">Enrollment Date: {detailStudent.enrollment_date ? new Date(detailStudent.enrollment_date).toLocaleDateString() : '-'}</Typography>
            <Typography variant="subtitle2">Status: {detailStudent.status}</Typography>
            <Typography variant="subtitle2">Notes: {detailStudent.notes || '-'}</Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Tags</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {(detailStudent.tags || []).map((tag: string, idx: number) => (
                  <Chip key={idx} label={tag} size="small" color="info" onDelete={() => handleDeleteTag(tag)} />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  label="Add Tag"
                  value={newTag}
                  onChange={e => setNewTag(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleAddTag(); }}
                />
                <Button variant="contained" onClick={handleAddTag}>Add</Button>
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Attendance Records</Typography>
              {timelineLoading ? (
                <Typography variant="body2">Loading...</Typography>
              ) : activityTimeline.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No attendance records found.</Typography>
              ) : (
                <List>
                  {activityTimeline.map((record, idx) => {
                    const dateObj = record.session_start ? new Date(record.session_start) : null;
                    const dayOfWeek = dateObj ? dateObj.toLocaleDateString(undefined, { weekday: 'long' }) : '';
                    const dateStr = dateObj ? dateObj.toLocaleDateString() : '';
                    return (
                      <ListItem key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1, borderRadius: 1, bgcolor: 'background.paper', boxShadow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, mr: 1 }}>{dayOfWeek}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>{dateStr}</Typography>
                          <Chip label={record.status} color={record.status === 'present' ? 'success' : 'default'} size="small" sx={{ ml: 'auto' }} />
                        </Box>
                        {record.course_title && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {record.course_title}
                          </Typography>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditStudent(detailStudent)} startIcon={<EditIcon />}>Edit</Button>
            <Button onClick={() => openDeleteDialog(detailStudent.id)} color="error" startIcon={<DeleteIcon />}>Delete</Button>
            <Button onClick={() => setDetailStudent(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  if (!isAuthenticated) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Students</Typography>
        <Alert severity="info">Please log in to view students.</Alert>
      </Box>
    );
  }

  if (!schoolId) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Students</Typography>
        <Alert severity="warning">No school selected. Please log in with a user associated with a school.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Students</Typography>
        <DataGridSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Students">
        <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Stack flex={1} direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
              <TextField
                fullWidth
                variant="outlined"
                size="medium"
                placeholder="Search by name or ID..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  style: { background: '#fff', borderRadius: 8 },
                }}
              />
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterDrawerOpen(true)}
                variant="outlined"
                sx={{ minWidth: 120 }}
              >
                Filters
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Add Student
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Export CSV
              </Button>
              <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportDialogOpen(true)} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Import CSV
              </Button>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Download Template
              </Button>
              <Button variant="contained" color="primary" onClick={handleOpenOnboardDialog} sx={{ mb: 2 }}>
                Onboard Students
              </Button>
            </Stack>
          </Stack>
          {/* Active filter chips below controls */}
          <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
            {filterClass && <Chip label={`Class: ${filterClass}`} onDelete={() => setFilterClass(null)} />}
            {filterStatus && <Chip label={`Status: ${filterStatus}`} onDelete={() => setFilterStatus(null)} />}
            {filterGender && <Chip label={`Gender: ${filterGender}`} onDelete={() => setFilterGender(null)} />}
            {filterEnrollmentStart && <Chip label={`Enrolled After: ${filterEnrollmentStart}`} onDelete={() => setFilterEnrollmentStart(null)} />}
            {filterEnrollmentEnd && <Chip label={`Enrolled Before: ${filterEnrollmentEnd}`} onDelete={() => setFilterEnrollmentEnd(null)} />}
          </Stack>
        </Paper>
      </PageHeader>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box sx={{ height: 700, width: '100%' }}>
        <DataGrid
          rows={filteredStudents}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          paginationMode="server"
          rowCount={totalStudents}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={loading}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          processRowUpdate={processRowUpdate}
          components={{
            Toolbar: GridToolbar,
            NoRowsOverlay: () => (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <CustomNoRowsOverlay message="No Students Found" />
                <img src="/logo192.png" alt="No students" style={{ width: 80, opacity: 0.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No students found. Click "Add Student" to get started!
                </Typography>
              </Box>
            )
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { 
                debounceMs: 500,
              },
            },
            filterPanel: {
              disableAddFilterButton: false,
            },
          }}
          filterMode="client"
          onRowClick={(params) => setDetailStudent(params.row)}
          disableColumnMenu={false}
          disableColumnFilter={false}
          disableColumnSelector={false}
          disableDensitySelector={false}
          hideFooter={false}
          hideToolbar={false}
          sx={{
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: 1,
            },

          }}
        />
      </Box>

      {editStudent && (
        <EditStudentDialog
          open={!!editStudent}
          onClose={() => setEditStudent(null)}
          student={editStudent}
          onSaved={fetchStudents}
        />
      )}

      <AddStudentDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        schoolId={schoolId}
        onSuccess={fetchStudents}
      />

      <Dialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, id: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this student? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmation({ open: false, id: null })}>
            Cancel
          </Button>
          <Button 
            onClick={() => deleteConfirmation.id && handleDelete(deleteConfirmation.id)} 
            color="error" 
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import Students from CSV</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>Instructions:</b><br/>
            1. Download the <Button size="small" onClick={handleDownloadTemplate}>CSV Template</Button>.<br/>
            2. Fill in student data. <b>Required columns:</b> name, class_level, status.<br/>
            3. <b>Status</b> must be one of: active, inactive, graduated, transferred.<br/>
            4. <b>Gender</b> (optional): Male, Female, Other.<br/>
            5. <b>Tags</b> can be comma-separated.<br/>
            6. Save as CSV and upload below.<br/>
          </DialogContentText>
          <input type="file" accept=".csv" onChange={handleImportCSV} style={{ marginTop: 16 }} />
          {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Import Summary Dialog */}
      <Dialog open={importSummary.success > 0 || importSummary.failed > 0} onClose={() => setImportSummary({ success: 0, failed: 0, failedRows: [] })}>
        <DialogTitle>Import Results</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <b>{importSummary.success}</b> students imported successfully.<br/>
            {importSummary.failed > 0 && <span><b>{importSummary.failed}</b> rows failed to import.</span>}
          </DialogContentText>
          {importSummary.failed > 0 && (
            <Button variant="outlined" onClick={handleDownloadFailedRows} sx={{ mt: 2 }}>
              Download Failed Rows
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportSummary({ success: 0, failed: 0, failedRows: [] })}>Close</Button>
        </DialogActions>
      </Dialog>

      <RenderDetailStudentDialog />

      {lightboxOpen && detailStudent?.profile_picture && (
        <Dialog open={lightboxOpen} onClose={() => setLightboxOpen(false)} maxWidth="xs">
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <img src={backendUrl + detailStudent.profile_picture} alt="Profile Large" style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 8 }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Filter Drawer */}
      <Drawer anchor="right" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 3 }}>
          <Typography variant="h6" gutterBottom>Advanced Filters</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Class Level</InputLabel>
            <Select value={filterClass || ''} onChange={e => setFilterClass(e.target.value || null)} label="Class Level">
              <MenuItem value="">All</MenuItem>
              {['Pre-Primary 1', 'Pre-Primary 2', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'].map(level => (
                <MenuItem key={level} value={level}>{level}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filterStatus || ''} onChange={e => setFilterStatus(e.target.value || null)} label="Status">
              <MenuItem value="">All</MenuItem>
              {['active', 'inactive', 'graduated', 'transferred'].map(status => (
                <MenuItem key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Gender</InputLabel>
            <Select value={filterGender || ''} onChange={e => setFilterGender(e.target.value || null)} label="Gender">
              <MenuItem value="">All</MenuItem>
              {['Male', 'Female', 'Other'].map(gender => (
                <MenuItem key={gender} value={gender}>{gender}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Enrolled After"
            type="date"
            value={filterEnrollmentStart || ''}
            onChange={e => setFilterEnrollmentStart(e.target.value || null)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Enrolled Before"
            type="date"
            value={filterEnrollmentEnd || ''}
            onChange={e => setFilterEnrollmentEnd(e.target.value || null)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <Button variant="contained" onClick={() => setFilterDrawerOpen(false)} fullWidth>Apply</Button>
        </Box>
      </Drawer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Dialog open={onboardDialogOpen} onClose={handleCloseOnboardDialog} maxWidth="md" fullWidth>
        <DialogTitle>Onboard Students Without User Accounts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              select
              fullWidth
              label="Filter by Class Level"
              value={onboardClassLevel}
              onChange={handleOnboardClassLevelChange}
            >
              <MenuItem value=''>All Class Levels</MenuItem>
              {classLevels.map(level => <MenuItem key={level} value={level}>{level}</MenuItem>)}
            </TextField>
          </Box>
          {onboardError && <Alert severity="error" sx={{ mb: 2 }}>{onboardError}</Alert>}
          {onboardSuccess && <Alert severity="success" sx={{ mb: 2 }}>{onboardSuccess}</Alert>}
          {unlinkedStudents.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: '#888', mt: 2 }}>No students to onboard.</Box>
          ) : (
            <Box>
              {unlinkedStudents.map(student => (
                <Paper key={student.id} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ fontWeight: 'bold', mb: 1 }}>{student.name} (Class: {student.class_level})</Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Username"
                      value={onboardForm[student.id]?.username || ''}
                      onChange={e => setOnboardForm(f => ({ ...f, [student.id]: { ...f[student.id], username: e.target.value } }))}
                      sx={{ flex: '1 1 30%' }}
                    />
                    <TextField
                      label="Email"
                      value={onboardForm[student.id]?.email || ''}
                      onChange={e => setOnboardForm(f => ({ ...f, [student.id]: { ...f[student.id], email: e.target.value } }))}
                      sx={{ flex: '1 1 30%' }}
                    />
                    <TextField
                      label="Password"
                      type="password"
                      value={onboardForm[student.id]?.password || ''}
                      onChange={e => setOnboardForm(f => ({ ...f, [student.id]: { ...f[student.id], password: e.target.value } }))}
                      sx={{ flex: '1 1 30%' }}
                    />
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleOnboard(student.id)}
                      disabled={onboarding[student.id]}
                      sx={{ flex: '1 1 10%', minWidth: 120 }}
                    >
                      {onboarding[student.id] ? 'Onboarding...' : 'Onboard'}
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOnboardDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Students; 